import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import lodashOmit from 'lodash/fp/omit';
import lodashKeyBy from 'lodash/fp/keyBy';
import lodashCompact from 'lodash/fp/compact';
import copyToClipboard from 'copy-to-clipboard';
import queryString from 'querystring';
import log from 'client/lib/log';

import typeCheck from 'common/util/typeCheck';

import {
  ANALYTICS_EVENT_ROOMMATE_REMOVED_FROM_HOUSEHOLD,
  ANALYTICS_EVENT_GUARANTOR_REMOVED_FROM_HOUSEHOLD,
} from 'client/analytics/eventList';
import {
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
  HOUSEHOLD_USER_REMOVAL_REASON_OPTION_ITEMS,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED,
  HOUSEHOLD_LISTING_STATUS_ACTIVE,
  HOUSEHOLD_STATUS_ACTIVE,
  HOUSEHOLD_STATUS_ARCHIVED,
  HOUSEHOLD_ARCHIVE_REASON_FOUND_PLACE_ON_THEIR_OWN,
  HOUSEHOLD_ARCHIVE_REASON_NO_RESPONSE,
  HOUSEHOLD_ARCHIVE_REASON_OTHER,
  HOUSEHOLD_REJECTED_REASON_OPTION_ITEMS,
} from 'common/householdConstants';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
  SR_API_ACTION_APPLY_APPLICATION_STATE_CHANGE,
  SR_API_ACTION_HOUSEHOLD_LISTING_ARCHIVE,
  SR_API_ACTION_HOUSEHOLD_REMOVE_USER,
  SR_API_ACTION_HOUSEHOLD_TAG_AS_APPLICATION_REJECTED,
  SR_API_ACTION_HOUSEHOLD_GENERATE_DOCS,
  SR_API_ACTION_HOUSEHOLD_UPDATE_LEAD_RENTER_ID,
  SR_API_ACTION_HOUSEHOLD_REMOVE_GUARANTOR,
  SR_API_ACTION_HOUSEHOLD_ARCHIVE,
  SR_API_ACTION_BITLY_CREATE_LINK,
  SR_API_ACTION_ADMIN_CREATE_JWT_TOKEN,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD,
} from 'common/dataTableConstants';

import {
  actionDataTableLoadItems,
} from 'client/helpers/dataTables';

import analytics from 'client/analytics';

import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrOptionSelectionModal from 'client/components/SrOptionSelectionModal';
import SrAssignHouseholdAdminSelect from 'client/components/SrAssignHouseholdAdminSelect';
import SrHouseholdDashboardCardListing from 'client/components/SrHouseholdDashboardCardListing';
import SrCopyText from 'client/components/SrCopyText';
import SrHouseholdNotesContainer from 'client/components/SrHouseholdNotesContainer';
import SrBadge from 'client/components/SrBadge';
import SrFrontConversation from 'client/components/SrFrontConversation';
import SrTenantAlertApplicationViewer from 'client/components/SrTenantAlertApplicationViewer';
import SrText from 'client/components/SrText';
import SrHouseholdTenantAlertStatusBox from 'client/components/SrHouseholdTenantAlertStatusBox/SrHouseholdTenantAlertStatusBox';

import postWithState from 'client/actions/postWithState';

import {
  getHouseholdListingsByStatus,
  extractHouseholdDisplayFields,
  orderHouseholdListingsByStrongestLink,
  makeSecurityDepositPaymentLink,
} from 'common/householdHelpers';

import {
  getApplicationMonthlyIncome,
} from 'common/householdApplication/applicationHelpers';

import {
  formatPrice,
} from 'common/formatters';
import { isListingTenantScreening } from 'common/listingHelpers';

const keyById = lodashKeyBy('id');
const keyByUserId = lodashKeyBy('userId');

class SrHouseholdDashboardCard extends Component {
  static propTypes = {
    household: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    onPhotosModalOpenClick: PropTypes.func.isRequired,
    onHouseholdLeaseSignedStatusUpdate: PropTypes.func.isRequired,
    onHouseholdPendingLandlordStatusUpdate: PropTypes.func.isRequired,
    householdAdminsList: PropTypes.array.isRequired,
    appUrl: PropTypes.string.isRequired,
    reloadDataTableHousehold: PropTypes.func,
    openBuyersAgentModal: PropTypes.func.isRequired,
    leaseStartDate: PropTypes.string,
  }

  constructor(props) {
    super(props);
    this.state = {
      applicationRegeneratingDocs: {},
      rejectedHouseholdListingId: null,
      showHouseholdUserRemovalReasons: null,
      runningTenantAlertByUserId: {},
    };
  }

  _onListingUnarchiveClick = (e) => {
    const { household, dispatch } = this.props;
    const householdId = household.id;
    const listingId = parseInt(e.target.dataset.listingId, 10);

    typeCheck('listingId::PositiveNumber', listingId);

    if (!global.confirm(`Are you sure you want to unarchive the listing "${listingId}"?`)) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'householdListings',
        where: {
          listingId: listingId,
          householdId: householdId,
        },
        updates: {
          status: HOUSEHOLD_LISTING_STATUS_ACTIVE,
          cancelReason: null,
          rejectedReason: null,
          archivedDate: null,
          unArchiveDate: new Date().toISOString(),
        },
      },
    }).then(() => {
      if (household.status === HOUSEHOLD_STATUS_ARCHIVED) {
        return dispatch(postWithState, {
          action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
          payload: {
            table: 'households',
            where: {
              id: household.id,
            },
            updates: {
              status: HOUSEHOLD_STATUS_ACTIVE,
              cancelReason: null,
            },
          },
        });
      }
    }).then(() => this._reloadDataTableHousehold(householdId));
  }

  _onApplicationStatusUpdate = ({
    applicationStatus,
    applicationId,
    householdId,
    listingId,
  }) => {
    const {
      onHouseholdLeaseSignedStatusUpdate,
      onHouseholdPendingLandlordStatusUpdate,
      dispatch,
    } = this.props;

    typeCheck('applicationStatus::NonEmptyString', applicationStatus);
    typeCheck('applicationId::PositiveNumber', applicationId);

    if (
      typeof onHouseholdLeaseSignedStatusUpdate === 'function'
      && applicationStatus === HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED
    ) {
      onHouseholdLeaseSignedStatusUpdate({
        householdId,
        listingId,
      });
    }
    else if (
      typeof onHouseholdPendingLandlordStatusUpdate === 'function'
      && applicationStatus === HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD
    ) {
      onHouseholdPendingLandlordStatusUpdate({
        householdId,
        listingId,
      });
    }
    else {
      dispatch(postWithState, {
        action: SR_API_ACTION_APPLY_APPLICATION_STATE_CHANGE,
        payload: {
          entityId: applicationId,
          toState: applicationStatus,
          householdId,
          listingId,
        },
      }).then(this._reloadDataTableHousehold);
    }
  }

  _onGenerateDocsButtonClick = (e) => {
    const { dispatch } = this.props;
    const applicationId = parseInt(e.target.dataset.applicationId, 10);
    const householdListingId = parseInt(e.target.dataset.householdListingId, 10);
    const category = e.target.dataset.category;
    typeCheck('applicationId::PositiveNumber', applicationId);
    typeCheck('householdListingId::PositiveNumber', householdListingId);
    typeCheck('category::NonEmptyString', category);

    const applicationRegeneratingDocsStateKey = `${applicationId}-${householdListingId}-${category}`;

    this.setState({
      applicationRegeneratingDocs: {
        ...this.state.applicationRegeneratingDocs,
        [applicationRegeneratingDocsStateKey]: true,
      },
    });

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_GENERATE_DOCS,
      queryId: applicationRegeneratingDocsStateKey,
      payload: {
        applicationId: applicationId,
        householdListingId: householdListingId,
        categories: [category],
      },
    })
      .then(this._reloadDataTableHousehold)
      .then(() => {
        this.setState({
          applicationRegeneratingDocs: lodashOmit(
            [applicationRegeneratingDocsStateKey],
            this.state.applicationRegeneratingDocs,
          ),
        });
      });
  }

  _reloadDataTableHousehold = (params) => {
    const { household, dispatch, reloadDataTableHousehold } = this.props;

    const householdIdToLoad = typeof (params || {}).householdIdToLoad === 'number'
      ? (params || {}).householdIdToLoad
      : household.id;

    return dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD,
      viewId: `household-id-${householdIdToLoad}`,
      filter: {
        'households.id': householdIdToLoad,
      },
      count: 1,
    }).then(() => {
      if (reloadDataTableHousehold) {
        reloadDataTableHousehold(householdIdToLoad);
      }
    });
  }

  _onUserFieldUpdate = ({ userId, field, update }) => {
    const { dispatch } = this.props;

    typeCheck('field::NonEmptyString', field);
    typeCheck('userId::PositiveNumber', userId);

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'users',
        where: {
          id: userId,
        },
        updates: {
          [field]: update,
        },
      },
    }).then(this._reloadDataTableHousehold);
  }

  _onRemoveUser = ({ userId, removalReason }) => {
    const { household, dispatch } = this.props;

    typeCheck('userId::PositiveNumber', userId);
    typeCheck('removalReason::NonEmptyString', removalReason);

    const user = household.users.find(({ id }) => id === userId);

    if (!global.confirm(`Are you sure you want to remove ${user.name} from this household?`)) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_REMOVE_USER,
      payload: {
        userId: userId,
        householdId: household.id,
      },
    }).then(() => {
      analytics.track(ANALYTICS_EVENT_ROOMMATE_REMOVED_FROM_HOUSEHOLD, {
        userId: userId,
        householdId: household.id,
        userRemovalReason: removalReason,
      });
      return this._reloadDataTableHousehold();
    });
  }

  _onListingArchiveClick = (e) => {
    const { household, dispatch } = this.props;
    const cancelReason = e.target.dataset.reason;
    const listingId = parseInt(e.target.dataset.listingId, 10);

    typeCheck('cancelReason::NonEmptyString', cancelReason);
    typeCheck('listingId::PositiveNumber', listingId);

    if (!global.confirm(
      `Are you sure you want to archive the listing "${listingId}" ` +
      `with "${cancelReason}"?`
    )) {
      return;
    }

    const { householdListingId } = household.listingApplications
      .find(({ listing }) => listing.id === listingId);

    if (cancelReason === HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED) {
      this.setState({
        rejectedHouseholdListingId: householdListingId,
      });
    }
    else {
      dispatch(postWithState, {
        action: SR_API_ACTION_HOUSEHOLD_LISTING_ARCHIVE,
        payload: {
          householdListingId,
          cancelReason,
        },
      }).then(this._reloadDataTableHousehold);
    }
  }

  _onHouseholdArchiveClick = (e) => {
    const { household, dispatch } = this.props;
    const cancelReason = e.target.dataset.reason;
    typeCheck('cancelReason::NonEmptyString', cancelReason);

    if (!global.confirm('Are you sure you want to archive this household?')) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_ARCHIVE,
      payload: {
        householdId: household.id,
        cancelReason: cancelReason,
      },
      onError: (error) => {
        global.alert(error.message);
      },
      onSuccess: this._reloadDataTableHousehold,
    });
  }

  _onHouseholdRejectedModalSubmit = (rejectedReason) => {
    const { household, dispatch } = this.props;
    const { rejectedHouseholdListingId } = this.state;

    typeCheck('showingFailureReason::NonEmptyString', rejectedReason);

    this.setState({
      rejectedHouseholdListingId: null,
    });

    if (!global.confirm(`Are you sure you want to update the failure reason to "${rejectedReason}"?`)) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_TAG_AS_APPLICATION_REJECTED,
      payload: {
        householdId: household.id,
      },
    });

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_LISTING_ARCHIVE,
      payload: {
        householdListingId: rejectedHouseholdListingId,
        cancelReason: HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED,
        rejectedReason: rejectedReason,
      },
    }).then(this._reloadDataTableHousehold);
  }

  _onHouseholdRejectedModalCancel = () => {
    this.setState({
      rejectedHouseholdListingId: null,
    });
  }

  _onMarkAsLeadRenterClick = (e) => {
    const { household, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_UPDATE_LEAD_RENTER_ID,
      payload: {
        householdId: household.id,
        leadRenterId: parseInt(e.target.dataset.userId, 10),
      },
    }).then(this._reloadDataTableHousehold);
  }

  _onShowHouseholdUserRemovalReasons = (e) => {
    this.setState({
      showHouseholdUserRemovalReasons: e.target.dataset.userId,
    });
  }

  _onHouseholdUserRemovalCancel = () => {
    this.setState({
      showHouseholdUserRemovalReasons: null,
    });
  }

  _onHouseholdUserRemovalSubmit = (removalReason) => {
    this._onRemoveUser({
      userId: parseInt(this.state.showHouseholdUserRemovalReasons, 10),
      removalReason: removalReason,
    });
    this._onHouseholdUserRemovalCancel();
  }

  _onGetGuarantorLinkClick = (e) => {
    const { household, appUrl, dispatch } = this.props;
    const renterName = e.target.dataset.renterName;
    const renterPhone = e.target.dataset.renterPhone;
    const renterId = parseInt(e.target.dataset.renterId, 10);
    const listingId = parseInt(e.target.dataset.listingId, 10);
    const listingAddress = e.target.dataset.listingAddress;

    const longUrl = (
      `${appUrl}/invite-guarantor?&name=${encodeURIComponent(renterName)}` +
      `&userId=${renterId}` +
      `&phone=${renterPhone}` +
      `&householdId=${household.id}` +
      `&listingId=${listingId}` +
      `&street=${listingAddress}`
    );

    dispatch(postWithState, {
      action: SR_API_ACTION_BITLY_CREATE_LINK,
      payload: {
        longUrl: longUrl,
      },
    }).then((bitlyLink) => {
      copyToClipboard(bitlyLink || longUrl);
    });
  }

  _onGetSecurityDepositLinkClick = (e) => {
    const { household, appUrl, dispatch } = this.props;
    const listingId = parseInt(e.target.dataset.listingId, 10);

    const longUrl = makeSecurityDepositPaymentLink({
      appUrl: appUrl,
      householdId: household.id,
      listingId: listingId,
    });

    dispatch(postWithState, {
      action: SR_API_ACTION_BITLY_CREATE_LINK,
      payload: {
        longUrl: longUrl,
      },
    }).then((bitlyLink) => {
      copyToClipboard(bitlyLink || longUrl);
    });
  }

  _onGetGuarantorApplicationPaymentLinkClick = (e) => {
    const { appUrl, dispatch } = this.props;
    const applicationId = parseInt(e.target.dataset.applicationId, 10);
    const listingId = parseInt(e.target.dataset.listingId, 10);
    const phone = e.target.dataset.phone;

    let autoLoginUrl;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_CREATE_JWT_TOKEN,
      payload: { phone },
    })
      .then((jwtToken) => {
        autoLoginUrl = `${appUrl}/token-login?${queryString.stringify({
          phone: phone,
          token: jwtToken,
          redirectTo: (
            `${appUrl}/application/payment?applicationId=${applicationId}&listingId=${listingId}`
          ),
        })}`;
        return dispatch(postWithState, {
          action: SR_API_ACTION_BITLY_CREATE_LINK,
          payload: {
            longUrl: autoLoginUrl,
          },
        });
      })
      .then((bitlyLink) => {
        copyToClipboard(bitlyLink || autoLoginUrl);
      })
      .catch((error) => {
        log.error(error);
      });
  }

  _onRemoveGuarantor = ({ guarantorUserId, renterUserId }) => {
    const { household, dispatch } = this.props;
    const user = household.guarantors.find(({ id }) => id === guarantorUserId);

    if (!global.confirm(`Are you sure you want to remove ${user.name} from this household?`)) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_REMOVE_GUARANTOR,
      payload: {
        guarantorUserId: guarantorUserId,
        renterUserId: renterUserId,
        householdId: household.id,
      },
    }).then(() => {
      analytics.track(ANALYTICS_EVENT_GUARANTOR_REMOVED_FROM_HOUSEHOLD, {
        guarantorUserId: guarantorUserId,
        renterUserId: renterUserId,
        householdId: household.id,
      });
      return this._reloadDataTableHousehold();
    });
  }

  _onToggleFollowUpCheckbox = () => {
    const { dispatch, household } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'households',
        where: {
          id: household.id,
        },
        updates: {
          followedUp: !household.followedUp,
        },
      },
    }).then(this._reloadDataTableHousehold);
  }

  _onRunningTenantAlert = (runningTenantAlertByUserId) => {
    this.setState({ runningTenantAlertByUserId });
  }

  render() {
    const {
      household,
      onPhotosModalOpenClick,
      openBuyersAgentModal,
      householdAdminsList,
      dispatch,
      appUrl,
      currentUser,
      leaseStartDate,
    } = this.props;

    const {
      rejectedHouseholdListingId,
      applicationRegeneratingDocs,
      showHouseholdUserRemovalReasons,
      runningTenantAlertByUserId,
    } = this.state;

    const {
      moveInDateDisplay,
      signUpDateDisplay,
      timeSinceLastFrontMessageDisplay,
    } = extractHouseholdDisplayFields({
      leaseStartDate,
      renterMoveInDate: household.renterMoveInDate,
      leadRenter: household.leadRenter,
    });

    const {
      householdListingsActive,
      householdListingsArchived,
    } = getHouseholdListingsByStatus({ household });

    const orderedHouseholdListingsActive = orderHouseholdListingsByStrongestLink({
      householdListings: householdListingsActive,
    });

    const userFrontConversationsMap = keyByUserId(household.userFrontConversations);
    const guarantorsById = keyById(household.guarantors);
    const tenantAlertReportsByUserId = keyByUserId(household.tenantAlertReports);
    const householdUsers = household.users;
    const activeHouseholdListings = orderHouseholdListingsByStrongestLink({
      householdListings: household.listingApplications
        .filter(({ householdListingStatus }) => {
          return householdListingStatus === HOUSEHOLD_LISTING_STATUS_ACTIVE;
        }),
    });
    const sunScreenActiveHouseholdListings = householdListingsActive
      .filter((householdListing) => isListingTenantScreening(householdListing.listing));
    const users = [];
    for (const user of householdUsers) {
      users.push(user);

      household.householdGuarantors
        .filter(({ renterUserId }) => renterUserId === user.id)
        .filter(({ guarantorUserId }) => guarantorsById[guarantorUserId])
        .forEach((householdGuarantor) => {
          users.push({
            ...guarantorsById[householdGuarantor.guarantorUserId],
            householdGuarantor,
          });
        });
    }

    return (
      <div className='tourCard household'>
        <div className='topContainer'>
          <div className='topInfo'>
            <span className='renterNameInfo'>
              <span className='renterName'>
                <div
                  className={classnames('checkbox', { isActive: household.followedUp })}
                  onClick={this._onToggleFollowUpCheckbox}
                />
                {household.users.map(({ name }) => name).join(', ')} ({household.assignedAdminUserId})
                <SrBadge>{household.marketDisplayName}</SrBadge>
              </span>
            </span>
            <div className='timestampContainer'>
              <span className='timestamp'>
                {`Last updated ${moment(household.lastUpdatedDate).calendar()}`}
              </span>
            </div>
            <div className='subhead'>
              <div>
                <span className='detail'>{moveInDateDisplay}</span>
                <span className='detail'>{signUpDateDisplay}</span>
              </div>
              {household?.leadRenter?.id && (
                <SrLink to={`/admin/household/all/${household.leadRenter.id}`} target='_blank'>
                  <SrBox marginRight='180px' marginTop='-3px'>
                    <SrText color='blue300' weight='500'>
                      View in new tab
                    </SrText>
                  </SrBox>
                </SrLink>
              )}
              <div>
                {timeSinceLastFrontMessageDisplay && (
                  <span className='detail'>{timeSinceLastFrontMessageDisplay}</span>
                )}
              </div>
            </div>
          </div>

          <div className='usersTable'>
            <table>
              <thead>
                <tr>
                  <th className='renter'>renter</th>
                  <th className='phone'>phone</th>
                  <th className='income'>monthly income</th>
                  <th className='credit'>credit</th>
                  <th className='front'>front</th>
                  <th className='chat'>messages</th>
                  <th className='email'>email</th>
                  <th className='actions'>renter actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const userConversation = userFrontConversationsMap[user.id];
                  const renterActions = [];
                  const tenantAlertReport = tenantAlertReportsByUserId[user.id];
                  const userApplication = (user.householdGuarantor
                    ? household.guarantorApplications.find((application) => application.userId === user.id)
                    : (orderedHouseholdListingsActive[0] || householdListingsArchived[0]).applications.find((application) => ( // eslint-disable-line max-len
                      application.userId === user.id
                    ))) || {};

                  if (
                    household.leadRenter.id !== user.id
                    && household.users.length > 1
                    && !user.householdGuarantor
                  ) {
                    renterActions.push({
                      userId: user.id,
                      onClick: this._onMarkAsLeadRenterClick,
                      label: 'mark as lead',
                      className: 'blue',
                    });
                  }

                  if (household.users.length > 1 && !user.householdGuarantor) {
                    renterActions.push({
                      userId: user.id,
                      onClick: this._onShowHouseholdUserRemovalReasons,
                      label: 'remove',
                    });
                  }

                  if (user.householdGuarantor) {
                    renterActions.push({
                      onClick: this._onRemoveGuarantor.bind(this, user.householdGuarantor),
                      label: 'remove',
                    });
                  }

                  const domKey = lodashCompact([
                    user.id,
                    user.householdGuarantor && user.householdGuarantor.renterUserId,
                    user.householdGuarantor && user.householdGuarantor.guarantorUserId,
                  ]).join('-');

                  return (
                    <tr key={domKey} className={classnames({ guarantor: !!user.householdGuarantor })}>
                      <td className='renter'>
                        {user.householdGuarantor && (
                          <div className='guarantorIcon' />
                        )}
                        <SrCopyText
                          text={user.id === household.leadRenter.id ? `Lead: ${user.name}` : user.name}
                        />
                      </td>
                      <td className='phone'>
                        <SrCopyText
                          text={user.phone}
                        />
                      </td>
                      <td className='income'>
                        {formatPrice(getApplicationMonthlyIncome({ householdApplication: userApplication }))}
                      </td>
                      <td className='credit'>
                        {tenantAlertReport
                          ? (
                            <SrTenantAlertApplicationViewer
                              user={user}
                              tenantAlertReport={tenantAlertReport}
                              onUpdateHousehold={this._reloadDataTableHousehold}
                            >
                              <SrText color='blue300' weight='medium' cursor='pointer'>
                                {tenantAlertReport.creditScore || 'None'}
                              </SrText>
                            </SrTenantAlertApplicationViewer>
                          )
                          : (runningTenantAlertByUserId[user.id]
                            ? 'Please wait...'
                            : (userApplication.creditScore || '--'))
                        }
                      </td>
                      <td className='front'>
                        {userConversation && (
                          <SrLink
                            className='linkOut front'
                            target='_blank'
                            to={`https://app.frontapp.com/open/${userConversation.conversationId}`}
                          >
                            Front
                          </SrLink>
                        )}
                        {!userConversation && (
                          <span className='empty'>--</span>
                        )}
                      </td>
                      <td className='front'>
                        {userConversation && (
                          <SrFrontConversation
                            frontConversation={userConversation}
                            user={user}
                            dispatch={dispatch}
                          />
                        )}
                        {!userConversation && (
                          <span className='empty'>--</span>
                        )}
                      </td>
                      <td className='email'>
                        {user.email && (
                          <SrCopyText
                            text={user.email}
                          />
                        )}
                        {!user.email && (
                          <span className='empty'>N/A</span>
                        )}
                      </td>
                      <td className='actions'>
                        <div className='deleteContainer archive'>
                          <span className='actionLabel'>Edit</span>
                          <div className='dropdownContainer'>
                            {renterActions.map((renterAction) => (
                              <div
                                key={renterAction.label}
                                className={classnames('option', renterAction.className)}
                                data-user-id={renterAction.userId}
                                data-user-name={renterAction.userName}
                                onClick={renterAction.onClick}
                              >
                                {renterAction.label}
                              </div>
                            ))}
                          </div>
                          {showHouseholdUserRemovalReasons && (
                            <SrOptionSelectionModal
                              headline='Choose Removal Reason'
                              optionItems={HOUSEHOLD_USER_REMOVAL_REASON_OPTION_ITEMS}
                              onSubmit={this._onHouseholdUserRemovalSubmit}
                              onCancel={this._onHouseholdUserRemovalCancel}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {activeHouseholdListings.length > 0 && (
          <SrBox padding='0 20px 20px' backgroundColor='rgba(123, 132, 136, 0.06)'>
            <SrHouseholdTenantAlertStatusBox
              listingId={activeHouseholdListings[0].listing.id}
              roommates={household.users}
              guarantors={household.guarantors}
              roommateApplications={activeHouseholdListings[0].applications}
              guarantorApplications={household.guarantorApplications}
              tenantAlertReports={household.tenantAlertReports}
              reloadHouseholdCard={this._reloadDataTableHousehold}
              onRunningTenantAlert={this._onRunningTenantAlert}
              dispatch={dispatch}
            />
          </SrBox>
        )}

        <div className='notesContainer'>
          <span className='notesTitle'>
            notes
          </span>

          <SrHouseholdNotesContainer
            household={household}
            reloadDataTable={this._reloadDataTableHousehold}
          />
        </div>

        <div className='listingsContainer'>
          {orderedHouseholdListingsActive.map((applicationListing, index) => {
            return (
              <SrHouseholdDashboardCardListing
                key={applicationListing.listing.id}
                applicationListing={applicationListing}
                household={household}
                index={index}
                users={users}
                appUrl={appUrl}
                currentUser={currentUser}
                applicationRegeneratingDocs={applicationRegeneratingDocs}
                onPhotosModalOpenClick={onPhotosModalOpenClick}
                openBuyersAgentModal={openBuyersAgentModal}
                onApplicationStatusUpdate={this._onApplicationStatusUpdate}
                onListingArchiveClick={this._onListingArchiveClick}
                onListingUnarchiveClick={this._onListingUnarchiveClick}
                onGetGuarantorLinkClick={this._onGetGuarantorLinkClick}
                onGetSecurityDepositLinkClick={this._onGetSecurityDepositLinkClick}
                onGetGuarantorApplicationPaymentLinkClick={this._onGetGuarantorApplicationPaymentLinkClick}
                onGenerateDocs={this._onGenerateDocsButtonClick}
                onUpdateHousehold={this._reloadDataTableHousehold}
                dispatch={dispatch}
              />
            );
          })}
          {householdListingsArchived.map((applicationListing, index) => {
            return (
              <SrHouseholdDashboardCardListing
                key={applicationListing.listing.id}
                applicationListing={applicationListing}
                household={household}
                index={index}
                users={users}
                appUrl={appUrl}
                currentUser={currentUser}
                applicationRegeneratingDocs={applicationRegeneratingDocs}
                onPhotosModalOpenClick={onPhotosModalOpenClick}
                openBuyersAgentModal={openBuyersAgentModal}
                onApplicationStatusUpdate={this._onApplicationStatusUpdate}
                onListingArchiveClick={this._onListingArchiveClick}
                onGetGuarantorLinkClick={this._onGetGuarantorLinkClick}
                onGetSecurityDepositLinkClick={this._onGetSecurityDepositLinkClick}
                onGetGuarantorApplicationPaymentLinkClick={this._onGetGuarantorApplicationPaymentLinkClick}
                onListingUnarchiveClick={this._onListingUnarchiveClick}
                onGenerateDocs={this._onGenerateDocsButtonClick}
                onUpdateHousehold={this._reloadDataTableHousehold}
                dispatch={dispatch}
              />
            );
          })}
        </div>

        <div className='bottomActions clearfix'>
          {householdAdminsList && (
            <div className='household-admin-select-container'>
              <SrAssignHouseholdAdminSelect
                householdId={household.id}
                assignedAdminUserId={household.assignedAdminUserId}
                reloadDataTable={this._reloadDataTableHousehold}
                householdAdminsList={householdAdminsList}
                dispatch={dispatch}
              />
            </div>
          )}
          {(!sunScreenActiveHouseholdListings.length && household.status === HOUSEHOLD_STATUS_ACTIVE) && (
            <div className='mainActionsContainer'>
              <div className='deleteContainer archive'>
                <span className='actionLabel'>Archive</span>
                <div className='dropdownContainer'>
                  <div
                    className='option'
                    data-reason={HOUSEHOLD_ARCHIVE_REASON_FOUND_PLACE_ON_THEIR_OWN}
                    onClick={this._onHouseholdArchiveClick}
                  >
                    found another place
                  </div>
                  <div
                    className='option'
                    data-reason={HOUSEHOLD_ARCHIVE_REASON_NO_RESPONSE}
                    onClick={this._onHouseholdArchiveClick}
                  >
                    lead unresponsive
                  </div>
                  <div
                    className='option'
                    data-reason={HOUSEHOLD_ARCHIVE_REASON_OTHER}
                    onClick={this._onHouseholdArchiveClick}
                  >
                    other
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {rejectedHouseholdListingId && (
          <SrOptionSelectionModal
            headline='Household Rejected Failure Reason'
            optionItems={HOUSEHOLD_REJECTED_REASON_OPTION_ITEMS}
            onSubmit={this._onHouseholdRejectedModalSubmit}
            onCancel={this._onHouseholdRejectedModalCancel}
            includeOther
          />
        )}
      </div>
    );
  }
}

SrHouseholdDashboardCard.displayName = 'SrHouseholdDashboardCard';

export default SrHouseholdDashboardCard;
